import { Controller } from '@hotwired/stimulus'

const DEFAULT_COLUMN = 'Division Name'
const DEFAULT_PARAM = 'confirmed_division_names[]'
const DESCRIPTION_COLUMN = 'Description'
const ACTIVE_COLUMN = 'Active'
const ACTIVE_VALUES = ['yes', 'y', 'true', 't', '1', 'active']
const INACTIVE_VALUES = ['no', 'n', 'false', 'f', '0', 'inactive']

export default class extends Controller {
  static targets = ['file', 'confirmation', 'fileName']

  static values = {
    detectUrl: String,
    columnName: String,
    paramName: String,
    detailsParamName: String,
    clearOnEmpty: Boolean,
    disableFileOnSubmit: Boolean,
  }

  connect() {
    if (!this.disableFileOnSubmitValue) return
    this.element.addEventListener('submit', () => {
      if (this.hasFileTarget) this.fileTarget.disabled = true
    })
  }

  openFilePicker() {
    this.fileTarget.click()
  }

  fileSelected() {
    const file = this.fileTarget.files[0]
    if (!file) return

    if (this.hasFileNameTarget) this.fileNameTarget.textContent = file.name

    const reader = new FileReader()
    reader.onload = (e) => {
      this.uploadErrors = []
      this.divisionDetails = this.#extractDivisionDetails(e.target.result)
      if (this.hasDetailsParamNameValue && this.uploadErrors.length) {
        this.#renderErrors()
        return
      }
      const names = Object.keys(this.divisionDetails)
      if (!names.length) {
        this.#renderPanel([])
        return
      }
      this.#detectNewDivisions(names)
    }
    reader.readAsText(file)
  }

  async #detectNewDivisions(names) {
    const body = new URLSearchParams()
    names.forEach((n) => body.append('names[]', n))
    body.append('authenticity_token', this.#csrfToken())

    try {
      const response = await fetch(this.detectUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
        body: body.toString(),
      })
      const data = await response.json()
      this.#renderPanel(data.new_divisions || [])
    } catch {
      this.#renderPanel([])
    }
  }

  #renderErrors() {
    const items = this.uploadErrors
      .map((error) => `<li>${this.#escapeHtml(error)}</li>`)
      .join('')

    this.confirmationTarget.innerHTML = `
      <div class="alert alert-danger" style="margin-top:10px;">
        <strong>This file can't be uploaded.</strong> Fix the following and choose the file again:
        <ul style="margin:8px 0 0 0;padding-left:20px;max-height:200px;overflow-y:auto;">${items}</ul>
      </div>`
  }

  #renderPanel(newDivisions) {
    const container = this.confirmationTarget
    const paramName = this.hasParamNameValue
      ? this.paramNameValue
      : DEFAULT_PARAM

    if (!newDivisions.length) {
      container.innerHTML = this.clearOnEmptyValue
        ? ''
        : '<div class="alert alert-info" style="margin-top:10px;">No new divisions found in this file.</div>'
      return
    }

    const items = newDivisions
      .map((name) => {
        const escaped = this.#escapeHtml(name)
        return `<li><label style="font-weight:normal;"><input type="checkbox" name="${paramName}" value="${escaped}" checked> ${escaped}${this.#detailsMarkup(name)}</label></li>`
      })
      .join('')

    container.innerHTML = `
      <div class="alert alert-warning" style="margin-top:10px;">
        <strong>New divisions to create:</strong> Uncheck any that should not be created.
        <div style="margin-top:8px;margin-bottom:4px;">
          <label style="font-weight:normal;margin-bottom:6px;display:block;">
            <input type="checkbox" data-bulk-division-upload-target="selectAll" checked> <em>Select all</em>
          </label>
          <ul style="margin:0 0 4px 0;list-style:none;padding-left:4px;max-height:200px;overflow-y:auto;">${items}</ul>
        </div>
      </div>`

    this.#wireSelectAll()
  }

  selectAllChanged() {
    const checked = this.selectAllTarget.checked
    this.#divisionCheckboxes().forEach((cb) => (cb.checked = checked))
  }

  divisionCheckboxChanged() {
    this.selectAllTarget.checked = this.#divisionCheckboxes().every(
      (cb) => cb.checked,
    )
  }

  #wireSelectAll() {
    const selectAll = this.confirmationTarget.querySelector(
      '[data-bulk-division-upload-target="selectAll"]',
    )
    const boxes = this.#divisionCheckboxes()

    selectAll.addEventListener('change', () => {
      boxes.forEach((cb) => (cb.checked = selectAll.checked))
    })
    boxes.forEach((cb) => {
      cb.addEventListener('change', () => {
        selectAll.checked = boxes.every((b) => b.checked)
      })
    })
  }

  #divisionCheckboxes() {
    const paramName = this.hasParamNameValue
      ? this.paramNameValue
      : DEFAULT_PARAM
    return [
      ...this.confirmationTarget.querySelectorAll(`input[name="${paramName}"]`),
    ]
  }

  #detailsMarkup(name) {
    if (!this.hasDetailsParamNameValue) return ''

    const { description, active } = this.divisionDetails[name] || {}
    const param = this.detailsParamNameValue
    const hidden = (key, value) =>
      `<input type="hidden" name="${param}[][${key}]" value="${this.#escapeHtml(value)}">`
    const descriptionText = description
      ? ` <span style="color:#888;">&mdash; ${this.#escapeHtml(description)}</span>`
      : ''
    const inactiveText = active
      ? ''
      : ' <span class="label label-default">Inactive</span>'

    return `${descriptionText}${inactiveText}${hidden('name', name)}${hidden('description', description || '')}${hidden('active', active ? 'true' : 'false')}`
  }

  #extractDivisionDetails(csvText) {
    const lines = csvText
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n')
    if (lines.length < 2) {
      this.uploadErrors.push('The file has no division rows.')
      return {}
    }
    const headers = this.#parseRow(lines[0]).map((h) => h.trim())
    const colName = this.hasColumnNameValue
      ? this.columnNameValue
      : DEFAULT_COLUMN
    const colIdx = headers.indexOf(colName)
    if (colIdx === -1) {
      this.uploadErrors.push(`The file must have a "${colName}" column.`)
      return {}
    }
    const descriptionIdx = headers.indexOf(DESCRIPTION_COLUMN)
    const activeIdx = headers.indexOf(ACTIVE_COLUMN)
    const details = {}
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue
      const cols = this.#parseRow(lines[i])
      const val = cols[colIdx]?.trim()
      if (!val || details[val]) continue
      const rawActive = activeIdx === -1 ? '' : (cols[activeIdx] || '').trim()
      const activeValue = rawActive.toLowerCase()
      if (
        activeValue &&
        !ACTIVE_VALUES.includes(activeValue) &&
        !INACTIVE_VALUES.includes(activeValue)
      ) {
        this.uploadErrors.push(
          `Row ${i + 1} (${val}): Active must be Yes/Y, No/N, or left blank, but was "${rawActive}". Blank divisions are created as active.`,
        )
      }
      details[val] = {
        description:
          descriptionIdx === -1 ? '' : cols[descriptionIdx]?.trim() || '',
        active: activeIdx === -1 || !INACTIVE_VALUES.includes(activeValue),
      }
    }
    return details
  }

  #parseRow(line) {
    const result = []
    let field = '',
      inQuotes = false
    for (const ch of line) {
      if (ch === '"') {
        inQuotes = !inQuotes
      } else if (ch === ',' && !inQuotes) {
        result.push(field)
        field = ''
      } else {
        field += ch
      }
    }
    result.push(field)
    return result
  }

  #csrfToken() {
    return (
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content') ?? ''
    )
  }

  #escapeHtml(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }
}
